import VaultInput from "shared-components/vault-input";
import { SnapCheckboxButton, SnapInput, SnapSelectMenu } from "suit";
import { DEFAULT_PERCENT_OWNERSHIP, TITLES } from "../const";
import React, { useEffect, useState } from "react";
import Address from "./address";

type KycBioInputProps = {
  entityPrefix: string;
  onStateSelect: (state: string) => void;
  onTitleSelect: (title: string) => void;
  onValid?: React.ComponentProps<typeof VaultInput>["onValid"];
  asksIsOwner?: boolean;
  askPercentOwnership?: boolean;
  onKycIsOwnerChange?: (isOwner: boolean) => void;
  onKycPercentOwnershipChange: (percentOwnership: number) => void;
};

export default function KycBioInput({
  entityPrefix,
  onStateSelect,
  onTitleSelect,
  onValid,
  asksIsOwner,
  askPercentOwnership,
  onKycIsOwnerChange,
  onKycPercentOwnershipChange,
}: KycBioInputProps) {
  const [firstNameIsValid, setFirstNameIsValid] = useState(false);
  const [lastNameIsValid, setLastNameIsValid] = useState(false);
  const [phoneIsValid, setPhoneIsValid] = useState(false);
  const [emailIsValid, setEmailIsValid] = useState(false);
  const [ssnIsValid, setSsnIsValid] = useState(false);
  const [dobMonthIsValid, setDobMonthIsValid] = useState(false);
  const [dobDayIsValid, setDobDayIsValid] = useState(false);
  const [dobYearIsValid, setDobYearIsValid] = useState(false);
  const [addressIsValid, setAddressIsValid] = useState(false);
  const [kycIsValid, setKycIsValid] = useState(false);
  const [kycIsOwner, setKycIsOwner] = useState(false);

  useEffect(() => {
    const newValidity =
      firstNameIsValid &&
      lastNameIsValid &&
      phoneIsValid &&
      emailIsValid &&
      ssnIsValid &&
      dobMonthIsValid &&
      dobDayIsValid &&
      dobYearIsValid &&
      addressIsValid;
    if (kycIsValid !== newValidity) {
      setKycIsValid(newValidity);
      if (onValid) {
        onValid(newValidity);
      }
    }
  }, [
    addressIsValid,
    dobDayIsValid,
    dobMonthIsValid,
    dobYearIsValid,
    emailIsValid,
    firstNameIsValid,
    kycIsValid,
    lastNameIsValid,
    onValid,
    phoneIsValid,
    ssnIsValid,
  ]);

  return (
    <>
      <div className="lg:grid grid-cols-3 lg:gap-6 flex flex-col lg:mt-4">
        <div className="w-full">
          <VaultInput
            name={`${entityPrefix}KycFirstName`}
            type="text"
            placeholder="First Name"
            label="First Name"
            onValid={setFirstNameIsValid}
          />
        </div>
        <div className="w-full">
          <VaultInput
            name={`${entityPrefix}KycLastName`}
            type="text"
            placeholder="Last Name"
            label="Last Name"
            onValid={setLastNameIsValid}
          />
        </div>
        <div className="w-full">
          <SnapSelectMenu
            placeholder="Title"
            label="Title"
            selectMenuOptions={TITLES}
            onSnap-select-menu-updated={(e) => {
              const selected = e.detail.find((option) => option.selected);
              if (selected?.value) {
                onTitleSelect(selected.value);
              }
            }}
          />
        </div>
      </div>
      <div className="lg:grid grid-cols-3 lg:gap-6 flex flex-col lg:mt-4">
        <div className="w-full">
          <VaultInput
            name={`${entityPrefix}KycPhone`}
            type="phone"
            placeholder="Phone Number"
            label="Phone Number"
            onValid={setPhoneIsValid}
          />
        </div>
        <div className="w-full">
          <VaultInput
            name={`${entityPrefix}KycEmail`}
            type="email"
            placeholder="Email"
            label="Email"
            onValid={setEmailIsValid}
          />
        </div>
        <div className="w-full">
          <VaultInput
            name={`${entityPrefix}KycSsn`}
            type="ssn"
            placeholder="SSN"
            label="SSN"
            onValid={setSsnIsValid}
          />
        </div>
      </div>
      <div className="lg:grid grid-cols-3 lg:gap-6 flex flex-col lg:mt-4">
        <div className="w-full">
          <VaultInput
            name={`${entityPrefix}KycDobMonth`}
            type="dobMonth"
            placeholder="Month"
            label="Date of Birth Month"
            onValid={setDobMonthIsValid}
          />
        </div>
        <div className="w-full">
          <VaultInput
            name={`${entityPrefix}KycDobDay`}
            type="dobDay"
            placeholder="Day"
            label="Day"
            onValid={setDobDayIsValid}
          />
        </div>
        <div className="w-full">
          <VaultInput
            name={`${entityPrefix}KycDobYear`}
            type="dobYear"
            placeholder="Year"
            label="Year"
            onValid={setDobYearIsValid}
          />
        </div>
      </div>
      {(asksIsOwner || askPercentOwnership) && (
        <div className="lg:grid grid-cols-2 lg:gap-6 flex flex-col lg:mt-4">
          {asksIsOwner && (
            <div className="w-full">
              <SnapCheckboxButton
                checked={kycIsOwner}
                onClick={(e) => {
                  setKycIsOwner(e.currentTarget.checked);
                  if (onKycIsOwnerChange) {
                    onKycIsOwnerChange(e.currentTarget.checked);
                  }
                }}
                label={`Do you own ${DEFAULT_PERCENT_OWNERSHIP}% or more of the business?`}
              />
            </div>
          )}
          {(kycIsOwner || askPercentOwnership) && (
            <div className="w-full">
              <SnapInput
                _id="percent_ownership"
                _type="number"
                value={`${DEFAULT_PERCENT_OWNERSHIP}`}
                label="Percent Ownership"
                onSnap-input-change={(e) => {
                  if (e?.detail?.target?.value) {
                    onKycPercentOwnershipChange(
                      parseInt(`${e.detail.target.value}`)
                    );
                  }
                }}
              />
            </div>
          )}
        </div>
      )}
      <Address
        entityPrefix={`${entityPrefix}Kyc`}
        onStateSelect={onStateSelect}
        onValid={setAddressIsValid}
      />
    </>
  );
}
